import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Pagination,
	Stack,
	Typography,
	alpha,
} from '@mui/material';
import { CloudUploadOutlined, ImageSearchOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { ITEM_IMAGE_ROLES, itemImageRemoteUrl } from '../../util/itemImageCache';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogContent,
	MoleDialogHeader,
	useMoleThemeColor,
} from '../MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import ImageEditor from './ImageEditor';
import {
	DocImageKind,
	HistoryImage,
	HistorySource,
	docImageSrc,
	fetchImageHistory,
	loadAsObjectUrl,
	uploadDocImage,
} from './docImagesApi';

const HISTORY_PER_PAGE = 12;

const SOURCE_COLOR: Record<HistorySource, 'default' | 'primary' | 'secondary'> = {
	CATALOG: 'default',
	INVOICE: 'primary',
	OFFER: 'secondary',
};

interface Props {
	open: boolean;
	kind: DocImageKind;
	itemId: number;
	itemName: string;
	onClose: () => void;
	/** The edited image, stored on the server. */
	onPicked: (picked: { image: string; url: string }) => void;
}

/** Upload, catalog gallery, or an image used before -- then always the editor. */
const LineImagePicker = ({ open, kind, itemId, itemName, onClose, onPicked }: Props) => {
	const { t } = useTranslation();
	const { api } = useAuth();
	const themeColor = useMoleThemeColor();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [dragOver, setDragOver] = useState(false);
	const [catalog, setCatalog] = useState<{ role: string; url: string }[] | null>(null);
	const [history, setHistory] = useState<HistoryImage[] | null>(null);
	const [historyTotal, setHistoryTotal] = useState(0);
	const [historyPage, setHistoryPage] = useState(1);
	const [editing, setEditing] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);

	const closeEditor = useCallback(() => {
		setEditing((current) => {
			if (current) URL.revokeObjectURL(current);
			return null;
		});
	}, []);

	const editBlob = useCallback((blob: Blob) => {
		if (!blob.type.startsWith('image/')) {
			toast.warn(t('common:docImages.upload.notImage'));
			return;
		}
		setEditing(URL.createObjectURL(blob));
	}, [t]);

	const editRemote = async (src: string) => {
		try {
			setEditing(await loadAsObjectUrl(src));
		} catch {
			toast.error(t('common:docImages.loadFailed'));
		}
	};

	// Catalog slots: probe each role, as the item gallery does.
	useEffect(() => {
		if (!open || !itemId) return;
		setCatalog(null);
		let cancelled = false;
		Promise.all(
			ITEM_IMAGE_ROLES.map(
				(role) =>
					new Promise<{ role: string; url: string } | null>((resolve) => {
						const url = `${itemImageRemoteUrl(itemId, role)}?t=${Date.now()}`;
						const img = new Image();
						img.onload = () => resolve({ role, url });
						img.onerror = () => resolve(null);
						img.src = url;
					}),
			),
		).then((found) => {
			if (!cancelled) setCatalog(found.filter(Boolean) as { role: string; url: string }[]);
		});
		return () => { cancelled = true; };
	}, [open, itemId]);

	useEffect(() => {
		if (open) setHistoryPage(1);
	}, [open, itemId]);

	useEffect(() => {
		if (!open || !itemId) return;
		setHistory(null);
		let cancelled = false;
		const load = async () => {
			try {
				const res = await fetchImageHistory(api, itemId, kind, historyPage, HISTORY_PER_PAGE);
				if (cancelled) return;
				if (res?.status === 200) {
					setHistory(res.data.rows);
					setHistoryTotal(res.data.total);
				} else {
					setHistory([]);
					toast.error(t('common:docImages.loadFailed'));
				}
			} catch {
				if (!cancelled) {
					setHistory([]);
					toast.error(t('common:docImages.loadFailed'));
				}
			}
		};
		void load();
		return () => { cancelled = true; };
	}, [open, itemId, kind, historyPage, api]);

	// Paste anywhere while the picker is open (and the editor is not).
	useEffect(() => {
		if (!open || editing) return;
		const onPaste = (event: ClipboardEvent) => {
			const file = Array.from(event.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
			if (!file) return;
			event.preventDefault();
			editBlob(file);
		};
		window.addEventListener('paste', onPaste);
		return () => window.removeEventListener('paste', onPaste);
	}, [open, editing, editBlob]);

	const handleEdited = async (blob: Blob) => {
		setUploading(true);
		try {
			const res = await uploadDocImage(api, blob);
			if (res?.status === 201) {
				closeEditor();
				onPicked(res.data);
			} else {
				toast.error(t('common:docImages.uploadFailed'));
			}
		} catch {
			toast.error(t('common:docImages.uploadFailed'));
		} finally {
			setUploading(false);
		}
	};

	const tileSx = {
		position: 'relative' as const,
		aspectRatio: '1 / 1',
		borderRadius: 2,
		overflow: 'hidden',
		border: '1px solid',
		borderColor: 'divider',
		bgcolor: 'background.paper',
		cursor: 'pointer',
		p: 0,
		transition: 'all 0.15s ease',
		'&:hover, &:focus-visible': { borderColor: themeColor, boxShadow: 3, outline: 'none' },
		'& img': { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
	};

	const tile = (key: string, src: string, source: HistorySource, onClick: () => void, caption?: string) => (
		<Box key={key} component="button" type="button" onClick={onClick} sx={tileSx} title={caption}>
			<img src={src} alt={caption ?? ''} loading="lazy" />
			<Chip
				label={t(`common:docImages.source.${source}`)}
				color={SOURCE_COLOR[source]}
				size="small"
				sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: 10, fontWeight: 800, letterSpacing: 0.4 }}
			/>
		</Box>
	);

	const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1.25 };

	return (
		<>
			<MoleDialog open={open} onClose={onClose} maxWidth="md" fullWidth themeColor={themeColor} sx={{ zIndex: Z_INDEX.deepDialog }}>
				<MoleDialogHeader
					title={t('common:docImages.pickerTitle')}
					subtitle={itemName}
					icon={<ImageSearchOutlined />}
					onClose={onClose}
					themeColor={themeColor}
				/>
				<MoleDialogContent>
					<Box
						onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
						onDragLeave={() => setDragOver(false)}
						onDrop={(event) => {
							event.preventDefault();
							setDragOver(false);
							const file = event.dataTransfer.files?.[0];
							if (file) editBlob(file);
						}}
						sx={{
							border: '2px dashed',
							borderColor: dragOver ? themeColor : alpha(themeColor, 0.35),
							bgcolor: alpha(themeColor, dragOver ? 0.1 : 0.04),
							borderRadius: 2.5,
							p: 3,
							textAlign: 'center',
							transition: 'all 0.15s ease',
						}}
					>
						<CloudUploadOutlined sx={{ fontSize: 36, color: themeColor }} />
						<Typography variant="subtitle1" fontWeight={700}>{t('common:docImages.upload.title')}</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							{t('common:docImages.upload.hint')}
						</Typography>
						<Button variant="outlined" onClick={() => fileInputRef.current?.click()} sx={{ color: themeColor, borderColor: themeColor }}>
							{t('common:docImages.upload.browse')}
						</Button>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							hidden
							onChange={(event) => {
								const file = event.target.files?.[0];
								event.target.value = '';
								if (file) editBlob(file);
							}}
						/>
					</Box>

					<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
						{t('common:docImages.catalog')}
					</Typography>
					{catalog === null ? (
						<CircularProgress size={22} sx={{ color: themeColor }} />
					) : catalog.length === 0 ? (
						<Typography variant="body2" color="text.secondary">{t('common:docImages.catalogEmpty')}</Typography>
					) : (
						<Box sx={grid}>
							{catalog.map(({ role, url }) => tile(role, url, 'CATALOG', () => void editRemote(url), role))}
						</Box>
					)}

					<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
						{t('common:docImages.history')}
					</Typography>
					{history === null ? (
						<CircularProgress size={22} sx={{ color: themeColor }} />
					) : history.length === 0 ? (
						<Typography variant="body2" color="text.secondary">{t('common:docImages.historyEmpty')}</Typography>
					) : (
						<>
							<Box sx={grid}>
								{history.map((row) =>
									tile(`${row.source}-${row.image}`, docImageSrc(row.url), row.source, () => void editRemote(docImageSrc(row.url))),
								)}
							</Box>
							{historyTotal > HISTORY_PER_PAGE && (
								<Stack alignItems="center" sx={{ mt: 1.5 }}>
									<Pagination
										count={Math.ceil(historyTotal / HISTORY_PER_PAGE)}
										page={historyPage}
										onChange={(_, page) => setHistoryPage(page)}
										size="small"
									/>
								</Stack>
							)}
						</>
					)}
				</MoleDialogContent>
				<MoleDialogActions>
					<MoleCancelButton onClick={onClose}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
				</MoleDialogActions>
			</MoleDialog>
			<ImageEditor src={editing} busy={uploading} onCancel={closeEditor} onDone={(blob) => void handleEdited(blob)} />
		</>
	);
};

export default LineImagePicker;
